// Ship 4 (Path γ) — TypeScript port of briefing_room_backend/engine/narrative.py.
//
// Generates human-readable strings for roll outcomes, wound descriptions,
// Skill Use Chart time/information mapping, Pain Resistance outcomes, and
// combat actions. These strings populate roll_log.narrative + GM-facing
// UI text + LLM prompt context.
//
// Source: manifest §2 (mechanic) + §8 (Skill Use Chart) + §10 (Wound
// Levels) + §11+ (combat narratives). Verbatim Core Rulebook
// %-truthfulness + time-multiplier semantics from p.32 SKILL USE CHART.

import { QR_NAMES } from './tables';
import type { ResolutionResult } from './resolution';

// ─── Roll outcome narrative ─────────────────────────────────────────────

/** Short result string for a resolved task. Suitable for roll_log.narrative. */
export function describeRoll(result: ResolutionResult, skillName?: string): string {
  const skillPart = skillName ? ` ${skillName}` : '';
  if (result.success) {
    const qrName = QR_NAMES[result.quality_rating as 1 | 2 | 3 | 4];
    return `${qrName}${skillPart} (rolled ${result.d100_roll} vs SC ${result.success_chance})`;
  }
  return `Failure${skillPart} (rolled ${result.d100_roll} vs SC ${result.success_chance})`;
}

// ─── Skill Use Chart messaging (manifest §8 / Core p.32) ────────────────

const _TIME_DESCRIPTIONS: Record<string, string> = {
  null: 'took 3× the base time (failure)',
  '4': 'took 2× the base time',
  '3': 'took the standard base time',
  '2': 'took half the base time',
  '1': 'took a quarter of the base time (quickest)',
};

/** Plain-language description of time taken per Skill Use Chart §8. */
export function describeTimeTaken(qr: 1 | 2 | 3 | 4 | null): string {
  if (qr !== null && ![1, 2, 3, 4].includes(qr)) {
    throw new Error(`QR must be 1-4 or null, got ${qr}`);
  }
  return _TIME_DESCRIPTIONS[qr === null ? 'null' : String(qr)]!;
}

const _INFORMATION_DESCRIPTIONS: Record<string, string> = {
  null: 'the information is completely false (failure)',
  '4': 'the information is half-truth, half-misinformation (50% true)',
  '3': 'the information is mostly accurate but contains some misinformation (75% true)',
  '2': 'the information is mostly accurate with minor errors (90% true)',
  '1': 'the information is fully reliable (100% true)',
};

/**
 * Plain-language description of information reliability per Skill Use
 * Chart §8 / Core p.32.
 */
export function describeInformation(qr: 1 | 2 | 3 | 4 | null): string {
  if (qr !== null && ![1, 2, 3, 4].includes(qr)) {
    throw new Error(`QR must be 1-4 or null, got ${qr}`);
  }
  return _INFORMATION_DESCRIPTIONS[qr === null ? 'null' : String(qr)]!;
}

// ─── Wound + scar narratives (manifest §10, §18-19) ─────────────────────

const _WOUND_DESCRIPTIONS: Record<string, string> = {
  ST: 'stunned',
  LW: 'lightly wounded',
  MW: 'moderately wounded',
  HW: 'heavily wounded',
  IN: 'incapacitated',
  KL: 'killed',
};

/** Short narrative for a wound event. */
export function describeWound(woundLevel: string, source?: string): string {
  if (!(woundLevel in _WOUND_DESCRIPTIONS)) {
    throw new Error(
      `Wound level must be one of ${Object.keys(_WOUND_DESCRIPTIONS).join(', ')}, got ${woundLevel}`,
    );
  }
  const base = _WOUND_DESCRIPTIONS[woundLevel]!;
  return source ? `${base} (${source})` : base;
}

/** Narrative for a permanent scar. */
export function describeScar(location: string, source?: string): string {
  if (source) {
    return `permanent scar on the ${location.toLowerCase()} (from ${source})`;
  }
  return `permanent scar on the ${location.toLowerCase()}`;
}

// ─── Pain Resistance (manifest §10 / Core p.47) ─────────────────────────

const _PAIN_RESISTANCE_EF: Record<string, number> = {
  LW: 7,
  MW: 5,
  HW: 3,
};

/**
 * Pain Resistance roll outcome narrative per manifest §10. Per Core
 * p.47: 'Pain Resistance roll (an Ease Factor 7 Willpower roll) to
 * resist the pain and continue taking actions.' MW uses EF5, HW uses
 * EF3.
 */
export function describePainResistanceOutcome(woundLevel: string, success: boolean): string {
  if (!(woundLevel in _PAIN_RESISTANCE_EF)) {
    throw new Error(`Pain Resistance only applies to LW/MW/HW, got ${woundLevel}`);
  }
  const ef = _PAIN_RESISTANCE_EF[woundLevel]!;
  if (success) {
    return `resisted the pain (EF${ef} Willpower roll succeeded)`;
  }
  return `overcome by pain (EF${ef} Willpower roll failed; no actions until next AR)`;
}

// ─── Combat action narrative builder ────────────────────────────────────

/** Combat action narrative builder for roll_log narrative + UI text. */
export function describeCombatAction(action: string, weapon?: string): string {
  const weaponPart = weapon ? ` with the ${weapon}` : '';
  return `${action}${weaponPart}`;
}

// ─── Composite roll-result narrative ────────────────────────────────────

/**
 * Build a multi-clause narrative for a roll outcome. Time + Information
 * clauses are appended after the QR-name clause if enabled.
 */
export function describeFullOutcome(
  result: ResolutionResult,
  options: {
    skillName?: string;
    includeTime?: boolean;
    includeInfo?: boolean;
  } = {},
): string {
  const { skillName, includeTime = true, includeInfo = false } = options;
  const parts: string[] = [describeRoll(result, skillName)];
  if (includeTime) {
    parts.push(describeTimeTaken(result.quality_rating));
  }
  if (includeInfo) {
    parts.push(describeInformation(result.quality_rating));
  }
  return parts.join('; ');
}

// ─── A8r Slice D (2026-06-04): Fame Recognition narrative ──────────────

/**
 * A8r Slice D — narrative string for a Fame Recognition outcome.
 *
 * Mirrors briefing_room_backend/engine/narrative.py describe_fame_recognition
 * verbatim. Engine-parity test asserts byte-for-byte equality between this
 * frontend implementation and the backend one.
 *
 * Priority order (highest wins):
 *   1. crossScenarioCarry=true → "<npc> recognizes Bond from a prior encounter"
 *   2. gmOverride !== null → "<npc> [does NOT] recognize[s] Bond (forced by GM)"
 *   3. d100Roll !== null → "<npc> [does NOT] recognize[s] Bond (rolled X vs SC Y)"
 *   4. Defensive fallback → "<npc> recognition state unknown"
 */
export function describeFameRecognition(
  npcName: string,
  successChance: number,
  d100Roll: number | null,
  recognized: boolean,
  gmOverride: boolean | null = null,
  crossScenarioCarry: boolean = false,
): string {
  if (crossScenarioCarry) {
    return `${npcName} recognizes Bond from a prior encounter`;
  }
  if (gmOverride !== null && gmOverride !== undefined) {
    const verb = recognized ? 'recognizes' : 'does NOT recognize';
    return `${npcName} ${verb} Bond (forced by GM)`;
  }
  if (d100Roll !== null && d100Roll !== undefined) {
    const verb = recognized ? 'recognizes' : 'does NOT recognize';
    return `${npcName} ${verb} Bond (rolled ${d100Roll} vs SC ${successChance})`;
  }
  return `${npcName} recognition state unknown`;
}
